/* eslint-disable no-undef */
/**
 * @providesModule SpectrophotometerData
 * @flow
 */
import React from 'react'
import { connect } from 'react-redux'

import { NonIdealState } from '@blueprintjs/core'

import SpectrophotometerChart from './SpectrophotometerChart'
import { updateFilter } from '../../redux/actions'

import type { Dispatch } from '../../redux/actions'
import type { State, SpectrophotometerReading } from '../../redux/state'

type OwnProps = { id: string, onValidityChange: boolean => void }
const mapStateToProps = ({ filtersById }: State, { id }: OwnProps) => {
  return {
    data: filtersById[id].spectrophotometerData
  }
}

const mapDispatchToProps = (dispatch: Dispatch, ownProps: OwnProps) => {
  return {
    onValidData: (spectrophotometerData: SpectrophotometerReading[]) => {
      dispatch(updateFilter(ownProps.id, { spectrophotometerData }))
    }
  }
}

type Props = OwnProps & {
  data: SpectrophotometerReading[],
  onValidData: SpectrophotometerReading[] => Promise<mixed>
}
type ComponentState = {
  csvText: string,
  error: ?string,
  showError: boolean
}
class SpectrophotometerData extends React.Component<Props, ComponentState> {
  state = {
    csvText: convertDataToCSV(this.props.data),
    error: null,
    showError: false
  }

  componentDidUpdate(prevProps: Props, prevState: ComponentState) {
    if (prevProps.id !== this.props.id) {
      this.setState({
        csvText: convertDataToCSV(this.props.data),
        error: null,
        showError: false
      })
      return
    }

    if (prevState.error !== this.state.error) {
      this.props.onValidityChange(this.state.error == null)
    }
  }

  _handleChange = (e: SyntheticInputEvent<*>) => {
    const csvText = e.target.value
    const { data, error } = parseCSV(csvText)

    this.setState({ csvText, error })
    if (error == null) {
      this.props.onValidData(data)
    }
  }

  _handleBlur = () => {
    if (this.state.error != null) {
      this.setState({ showError: true })
    }
  }

  render() {
    const { data } = this.props
    const { csvText, error, showError } = this.state

    return (
      <div className="pt-card" style={{ marginTop: '2em' }}>
        <h5>Spectrophotometer Data</h5>
        <div style={{ display: 'flex' }}>
          <div style={{ flex: 1 }}>
            <textarea
              className={`pt-input ${error != null && showError
                ? 'pt-intent-danger'
                : ''}`}
              style={{ width: '100%', height: 200 }}
              value={csvText}
              onChange={this._handleChange}
              onBlur={this._handleBlur}
            />
            {error != null && showError && (
              <div className="pt-form-helper-text" style={{ color: '#db3737' }}>
                {error}
              </div>
            )}
          </div>
          <div
            style={{
              flex: 3,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}
          >
            {data && data.length > 0 ? (
              <SpectrophotometerChart data={data} />
            ) : (
              <NonIdealState
                title="No Data"
                description="Paste wavelength, OD, and %T data from Excel."
                visual="timeline-line-chart"
              />
            )}
          </div>
        </div>
      </div>
    )
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(
  SpectrophotometerData
)

function convertDataToCSV(data = []) {
  return data
    .map(row => `${row.wavelength},${row.od},${row.transmittance}`)
    .join('\n')
}

function parseCSV(
  csv: string
): { data: SpectrophotometerReading[], error: ?string } {
  const data: SpectrophotometerReading[] = []
  const rows = csv.split('\n')

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i].trim()
    if (row === '') continue

    const columns = row.split(/[, \t]+/)
    if (columns.length !== 3 || columns.some(column => column === '')) {
      return {
        data: [],
        error: `Line ${i + 1} must contain wavelength, OD, and %T.`
      }
    }

    const wavelength = Number(columns[0])
    const od = Number(columns[1])
    const transmittance = Number(columns[2])
    if (
      isNaN(wavelength) ||
      isNaN(od) ||
      isNaN(transmittance) ||
      !isFinite(wavelength) ||
      !isFinite(od) ||
      !isFinite(transmittance) ||
      wavelength % 1 !== 0
    ) {
      return {
        data: [],
        error: `Line ${i + 1} must contain valid numbers.`
      }
    }

    data.push({ wavelength, od, transmittance })
  }

  return { data, error: null }
}
